import { FC } from 'react'
import { ArrowRight } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'

const certMeta: CardMeta = {
  description: 'Certificates expiring soon or overdue for renewal.',
  endpoint: 'GET /qhse/certificates (placeholder — no bulk expiry endpoint)',
  logic: 'Placeholder — shows a message until a dedicated expiry endpoint is available.',
}

const wpMeta: CardMeta = {
  description: 'Open work permits requiring attention.',
  endpoint: 'GET /qhse/work-permits',
  logic: 'Displays work permits with status "SUBMIT" or "SAVE".',
}

const complianceMeta: CardMeta = {
  description: 'Environmental compliance and risk assessment overview.',
  endpoint: 'Various QHSE APIs',
  logic: 'Placeholder — future integration with MARPOL, risk assessments, and month-end reports.',
}

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

interface Props {
  workPermits: any[]
  onViewWorkPermits: () => void
}

const CertificateWorkPermitPanel: FC<Props> = ({ workPermits, onViewWorkPermits }) => {
  const recentPermits = workPermits.slice(0, 4)

  return (
    <>
      {/* Expiring Certificates */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
        <div className='flex items-center justify-between mb-3'>
          <h6 className='text-sm font-bold text-slate-900'>Expiring Certificates</h6>
          <CardInfoButton meta={certMeta} />
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <p className='text-[10px] text-slate-400 text-center py-6'>
            Certificate expiry data will appear here when a bulk expiry endpoint is available.
          </p>
        </div>
      </div>

      {/* Open Work Permits */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-2'>
            <h6 className='text-sm font-bold text-slate-900'>Open Work Permits</h6>
            <CardInfoButton meta={wpMeta} />
          </div>
          <button onClick={onViewWorkPermits} className='text-[10px] font-semibold text-blue-600 hover:text-blue-800 transition-colors'>
            View all <ArrowRight className='w-3 h-3 inline' />
          </button>
        </div>
        <div className='overflow-x-auto max-h-[200px] overflow-y-auto'>
          <table className='w-full text-[11px]'>
            <thead>
              <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                <th className='text-left font-semibold py-2 pr-2'>Permit</th>
                <th className='text-left font-semibold py-2 px-2'>Vessel</th>
                <th className='text-left font-semibold py-2 px-2'>Start</th>
                <th className='text-right font-semibold py-2 pl-2'>Status</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {recentPermits.length === 0 ? (
                <tr>
                  <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No work permits found</td>
                </tr>
              ) : (
                recentPermits.map((wp, i) => (
                  <tr key={wp.id ?? i} className='hover:bg-slate-50 transition-colors'>
                    <td className='py-2.5 pr-2 font-semibold text-slate-800'>{wp.permitName || wp.formNumber || `#${wp.id}`}</td>
                    <td className='py-2.5 px-2 text-slate-500'>{wp.vesselName || '—'}</td>
                    <td className='py-2.5 px-2 text-slate-700'>{fmtDate(wp.startDate)}</td>
                    <td className='py-2.5 pl-2 text-right'>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                        wp.status === 'CLOSE' ? 'bg-emerald-50 text-emerald-600' :
                        wp.status === 'SUBMIT' ? 'bg-amber-50 text-amber-600' :
                        'bg-slate-50 text-slate-600'
                      }`}>{wp.status || 'SAVE'}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Risk / Compliance */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
        <div className='flex items-center justify-between mb-3'>
          <h6 className='text-sm font-bold text-slate-900'>Risk & Compliance</h6>
          <CardInfoButton meta={complianceMeta} />
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <p className='text-[10px] text-slate-400 text-center py-6'>
            Risk assessments, MARPOL compliance, and month-end report status will appear here.
          </p>
        </div>
      </div>
    </>
  )
}

export default CertificateWorkPermitPanel
